import { useMemo, useState, useCallback, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useSearch } from "wouter";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { useTenant } from "@/contexts/TenantContext";
import { useSession } from "@/hooks/useSession";
import { publicApi } from "@/lib/api";
import { trackPageView, trackEvent } from "@/lib/analytics";
import { SEO, SEOPresets } from "@/components/shared/SEO";
import { HoldTimer } from "@/components/shared/HoldTimer";
import { PassengerForm } from "@/components/checkout/PassengerForm";
import { BookingSummary } from "@/components/checkout/BookingSummary";
import { PromoCodeInput, ValidatedDiscount } from "@/components/checkout/PromoCodeInput";
import { TermsCheckbox } from "@/components/checkout/TermsCheckbox";
import { EnhancedHeader } from "@/components/layout/EnhancedHeader";
import { DEFAULT_TIMEZONE } from "@shared/timezone-utils";
import { computePublicBatchPricing } from "@shared/public-checkout-pricing";

interface EligibilityResponse {
  eligible: boolean;
  message?: string;
}

interface City {
  id: number;
  name: string;
  state: string | null;
}

interface TripDetails {
  id: number;
  departureTime: string;
  arrivalTime: string | null;
  availableSeats: number;
  segmentId: number;
  segmentPrice: string;
  roundTripPrice: string | null;
  routeName: string;
  /**
   * The PASSENGER's own boarding/alighting for this segment, resolved
   * server-side (PR B3) by the same code that produces the ticket. Rendering
   * these keeps search, seat selection, checkout and the ticket showing one
   * consistent set of times. Null = genuinely unknown, never a substitute.
   */
  boardingIso: string | null;
  boardingTimeFormatted: string | null;
  boardingTimezone: string;
  boardingAbbr: string | null;
  alightingIso: string | null;
  alightingTimeFormatted: string | null;
  alightingTimezone: string;
  alightingAbbr: string | null;
}

interface PassengerData {
  fullName: string;
  phone: string;
  email: string;
  dateOfBirth: string;
}

interface CheckoutFormData {
  passengers: PassengerData[];
}

interface SeatHold {
  id: number;
  seatNumber: string;
  expiresAt: string;
}

export function CheckoutPage() {
  const { t } = useTranslation();
  const { slug, tenant } = useTenant();
  const { sessionId, clearSession } = useSession();
  const [, setLocation] = useLocation();
  const searchString = useSearch();
  const searchParams = new URLSearchParams(searchString);

  // Parse query params
  const tripId = parseInt(searchParams.get("tripId") || "0", 10);
  const seats = searchParams.get("seats")?.split(",").filter(Boolean) || [];
  const passengers = parseInt(searchParams.get("passengers") || "1", 10);
  const tripType = (searchParams.get("tripType") || "one-way") as "one-way" | "round-trip";
  const date = searchParams.get("date") || "";
  const fromId = searchParams.get("from") || "";
  const toId = searchParams.get("to") || "";
  const price = parseFloat(searchParams.get("price") || "0");

  // State for applied discount
  const [appliedDiscount, setAppliedDiscount] = useState<ValidatedDiscount | null>(null);

  // State for terms acceptance
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState<string | null>(null);

  // State for passenger eligibility check
  const [eligibilityError, setEligibilityError] = useState<string | null>(null);
  const [isCheckingEligibility, setIsCheckingEligibility] = useState(false);

  // Discount amount via the SHARED pricing module — the exact math the
  // server uses to recompute the batch total against the Stripe charge.
  // Per-ticket semantics (percent: pct off each fare; amount: capped value
  // off each fare), matching what booking rows have always recorded. Any
  // local math here would drift from the server and get the paid batch
  // rejected.
  const discountAmount = useMemo(() => {
    if (!appliedDiscount) return 0;
    return computePublicBatchPricing(price, seats.length, {
      id: appliedDiscount.id,
      valueType: appliedDiscount.valueType,
      value: parseFloat(appliedDiscount.value),
    }).totalDiscount;
  }, [appliedDiscount, price, seats.length]);

  // Callback for when discount is applied/removed
  const handleDiscountChange = useCallback((discount: ValidatedDiscount | null) => {
    setAppliedDiscount(discount);
  }, []);

  // Get tenant timezone
  const timezone = tenant?.timezone || DEFAULT_TIMEZONE;

  // Initialize form with empty passenger data for each seat
  const defaultValues = useMemo(() => ({
    passengers: seats.map(() => ({
      fullName: "",
      phone: "",
      email: "",
      dateOfBirth: "",
    })),
  }), [seats.length]);

  // mode "all" (not "onBlur"): the submit button is disabled on !isValid, and
  // onBlur-only validation leaves isValid stale until the last-touched field
  // blurs. Clicking a disabled button doesn't blur the focused input, so a
  // group-booking user whose final action was typing the last passenger name
  // could never enable the button without clicking empty space first.
  const methods = useForm<CheckoutFormData>({
    defaultValues,
    mode: "all",
  });

  const { handleSubmit, formState: { isValid, errors }, watch } = methods;

  // Watch first passenger's phone and email for primary contact validation
  const firstPassengerPhone = watch("passengers.0.phone");
  const firstPassengerEmail = watch("passengers.0.email");
  const hasPrimaryContact = !!(firstPassengerPhone || firstPassengerEmail);

  // Check if terms must be accepted (only if tenant has T&C configured)
  const requiresTerms = !!(tenant?.termsAndConditionsUrl || tenant?.termsAndConditionsText);
  const termsValid = !requiresTerms || termsAccepted;

  // Track page view and checkout_started event
  useEffect(() => {
    if (slug && tripId && seats.length > 0) {
      trackPageView("checkout", slug);
      trackEvent("checkout_started", {
        tripId,
        seatCount: seats.length,
        totalPrice: price * seats.length,
        hasDiscount: false, // Will be tracked separately if discount is applied
      });
    }
  }, [slug, tripId, seats.length, price]);

  // Fetch cities for display
  const { data: cities = [] } = useQuery({
    queryKey: ["cities", slug],
    queryFn: async () => {
      if (!slug) return [];
      return publicApi.get<City[]>(`/api/public/${slug}/cities`);
    },
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch trip details
  const { data: trips = [] } = useQuery({
    queryKey: ["trips", slug, date, fromId, toId, passengers],
    queryFn: async () => {
      if (!slug || !date || !fromId || !toId) return [];
      const queryParams = new URLSearchParams({
        originCityId: fromId,
        destinationCityId: toId,
        date,
        passengers: passengers.toString(),
      });
      return publicApi.get<TripDetails[]>(
        `/api/public/${slug}/trips/search?${queryParams.toString()}`
      );
    },
    enabled: !!slug && !!date && !!fromId && !!toId,
    staleTime: 30 * 1000,
  });

  // Fetch seat holds to get expiration time
  const { data: seatHolds } = useQuery({
    queryKey: ["seat-holds", slug, tripId, sessionId],
    queryFn: async () => {
      if (!slug || !tripId || !sessionId) return [];
      return publicApi.get<SeatHold[]>(
        `/api/public/${slug}/seat-holds?tripId=${tripId}&sessionId=${sessionId}`
      );
    },
    enabled: !!slug && !!tripId && !!sessionId,
    staleTime: 10 * 1000,
  });

  // Get trip details from search results
  const tripDetails = trips.find((t) => t.id === tripId);

  // Get city names
  const originCity = cities.find((c) => c.id.toString() === fromId);
  const destinationCity = cities.find((c) => c.id.toString() === toId);

  // Get earliest hold expiration
  const holdExpiresAt = useMemo(() => {
    if (!seatHolds || seatHolds.length === 0) return null;
    const expirations = seatHolds
      .map((h) => new Date(h.expiresAt).getTime())
      .filter((t) => !isNaN(t));
    if (expirations.length === 0) return null;
    const earliest = Math.min(...expirations);
    return new Date(earliest).toISOString();
  }, [seatHolds]);

  // Custom validation for primary contact (first passenger must have phone OR email)
  const validatePrimaryContact = () => {
    const firstPassenger = methods.getValues("passengers.0");
    if (!firstPassenger?.phone && !firstPassenger?.email) {
      methods.setError("passengers.0.phone", {
        type: "manual",
        message: t("checkout.primaryContactRequired"),
      });
      methods.setError("passengers.0.email", {
        type: "manual",
        message: t("checkout.primaryContactRequired"),
      });
      return false;
    }
    return true;
  };

  const handleBackToSeats = () => {
    const params = new URLSearchParams({
      passengers: passengers.toString(),
      tripType,
      date,
      from: fromId,
      to: toId,
      price: price.toString(),
    });
    setLocation(`/trips/${tripId}/seats?${params.toString()}`);
  };

  const onSubmit = async (data: CheckoutFormData) => {
    // Guard against double-submit while eligibility check is in flight
    if (isCheckingEligibility) return;

    // Validate primary contact
    if (!validatePrimaryContact()) {
      return;
    }

    // Validate terms acceptance (if required)
    if (requiresTerms && !termsAccepted) {
      setTermsError(t("checkout.terms.required"));
      return;
    }
    setTermsError(null);

    // Clear any prior eligibility error before re-checking
    setEligibilityError(null);

    // Check passenger eligibility before navigating to payment.
    // Collect non-empty phone numbers from all passengers.
    const phones = data.passengers.map((p) => p.phone).filter(Boolean);
    if (phones.length > 0) {
      setIsCheckingEligibility(true);
      try {
        const res = await publicApi.post<EligibilityResponse>(
          `/api/public/${slug}/passengers/eligibility`,
          { phones }
        );
        if (!res.eligible) {
          setEligibilityError(res.message || t("checkout.eligibilityBlocked"));
          setIsCheckingEligibility(false);
          return;
        }
      } catch (err) {
        // Fail open: a transient network error should not block all public
        // sales. Log the failure and allow navigation to proceed.
        console.error("[eligibility] check failed, failing open:", err);
      } finally {
        setIsCheckingEligibility(false);
      }
    }

    // Build payment page URL with all necessary data
    const paymentParams = new URLSearchParams({
      tripId: tripId.toString(),
      seats: seats.join(","),
      tripType,
      date,
      from: fromId,
      to: toId,
      price: price.toString(),
      segmentId: tripDetails?.segmentId?.toString() || "",
      originCity: originCity?.name || "",
      destinationCity: destinationCity?.name || "",
      routeName: tripDetails?.routeName || "",
      departureTime: tripDetails?.boardingIso || "",
      arrivalTime: tripDetails?.alightingIso || "",
      // Carry each end's own zone through to payment, so the clocks the
      // passenger confirmed on checkout are the clocks they pay against.
      departureTimezone: tripDetails?.boardingTimezone || "",
      arrivalTimezone: tripDetails?.alightingTimezone || "",
      holdExpiresAt: holdExpiresAt || "",
      passengers: encodeURIComponent(JSON.stringify(data.passengers)),
    });

    // Add discount info if present (id is a UUID string — pass verbatim)
    if (appliedDiscount) {
      paymentParams.set("discountAmount", discountAmount.toString());
      paymentParams.set("discountCode", appliedDiscount.label || "");
      paymentParams.set("discountId", appliedDiscount.id);
    }

    setLocation(`/payment?${paymentParams.toString()}`);
  };

  const handleHoldExpire = () => {
    clearSession();
    setLocation("/");
  };

  if (!tripId || seats.length === 0) {
    return (
      <div className="min-h-screen bg-stone-50 text-ink flex flex-col">
        <EnhancedHeader />
        <main className="flex-1 flex items-center justify-center px-6 py-12">
          <div className="max-w-md text-center">
            <h2 className="font-display text-display-md text-ink mb-2">
              {t("errors.invalidBooking")}
            </h2>
            <p className="text-ink-muted mb-6">{t("errors.missingInfo")}</p>
            <button
              onClick={() => setLocation("/")}
              className="px-5 py-2.5 bg-ink text-stone-50 hover:bg-brand transition-colors focus-visible-ring"
            >
              {t("trips.backToSearch")}
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 text-ink flex flex-col">
      <SEO {...SEOPresets.checkout} />

      <EnhancedHeader />

      {/* Context strip */}
      <section className="bg-white border-b hairline-strong">
        <div className="mx-auto max-w-5xl px-6 py-5 md:py-6">
          <button
            onClick={handleBackToSeats}
            className="text-eyebrow uppercase text-ink-muted hover:text-ink inline-flex items-center gap-1.5 mb-2 transition-colors focus-visible-ring"
          >
            <ArrowLeft className="h-3 w-3" />
            {t("seats.backToResults")}
          </button>
          <h1 className="font-display text-[1.65rem] md:text-[2rem] leading-tight text-ink">
            {t("checkout.passengerDetails")}
          </h1>
        </div>
      </section>

      {/* Hold timer banner */}
      {holdExpiresAt && (
        <div className="bg-brand-soft border-b hairline-strong">
          <div className="mx-auto max-w-5xl px-6 py-3">
            <HoldTimer
              expiresAt={holdExpiresAt}
              onExpire={handleHoldExpire}
            />
          </div>
        </div>
      )}

      <main id="main-content" className="flex-1">
        <div className="mx-auto max-w-5xl px-6 py-8 md:py-10">
          <div className="grid gap-8 lg:grid-cols-[1fr_360px]">
            {/* Passenger forms column */}
            <div className="space-y-5">
              <p className="text-sm text-ink-muted">
                {t("checkout.primaryContactRequired")}
              </p>

              <FormProvider {...methods}>
                <form
                  id="checkout-form"
                  onSubmit={handleSubmit(onSubmit)}
                  className="space-y-5"
                >
                  {seats.map((seatNumber, index) => (
                    <PassengerForm
                      key={`${seatNumber}-${index}`}
                      index={index}
                      seatNumber={seatNumber}
                      isPrimary={index === 0}
                    />
                  ))}

                  {errors.passengers?.[0]?.phone &&
                    errors.passengers?.[0]?.email && (
                      <p className="text-sm text-destructive" role="alert">
                        {t("checkout.primaryContactRequired")}
                      </p>
                    )}

                  {eligibilityError && (
                    <p className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded px-3 py-2" role="alert">
                      {eligibilityError}
                    </p>
                  )}

                  {/* Mobile: terms + submit inline */}
                  <div className="lg:hidden space-y-4">
                    <div className="bg-white border hairline-strong p-4">
                      <TermsCheckbox
                        id="terms-mobile"
                        checked={termsAccepted}
                        onCheckedChange={(checked) => {
                          setTermsAccepted(checked);
                          if (checked) setTermsError(null);
                        }}
                        error={termsError || undefined}
                      />
                    </div>
                    <button
                      type="submit"
                      className="w-full h-14 bg-brand text-brand-ink font-semibold text-[0.9rem] uppercase tracking-[0.12em] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-ink transition-[background] focus-visible-ring"
                      disabled={
                        !isValid || !hasPrimaryContact || !termsValid || isCheckingEligibility
                      }
                      data-testid="checkout-continue-mobile"
                    >
                      {isCheckingEligibility
                        ? t("checkout.checkingEligibility")
                        : t("checkout.continueToPayment")}
                    </button>
                  </div>
                </form>
              </FormProvider>
            </div>

            {/* Right column — live ticket + promo + terms + submit */}
            <aside className="lg:sticky lg:top-6 h-fit space-y-4">
              <BookingSummary
                originCity={originCity?.name || t("common.loading")}
                destinationCity={destinationCity?.name || t("common.loading")}
                date={date}
                departureTime={tripDetails?.boardingIso ?? undefined}
                arrivalTime={tripDetails?.alightingIso ?? undefined}
                timezone={timezone}
                departureTimezone={tripDetails?.boardingTimezone}
                arrivalTimezone={tripDetails?.alightingTimezone}
                seats={seats}
                pricePerPassenger={price}
                tripType={tripType}
                discountAmount={discountAmount}
                discountCode={appliedDiscount?.label}
              />

              <div className="bg-white border hairline-strong p-4">
                <PromoCodeInput onDiscountApplied={handleDiscountChange} />
              </div>

              <div className="hidden lg:block bg-white border hairline-strong p-4">
                <TermsCheckbox
                  id="terms-desktop"
                  checked={termsAccepted}
                  onCheckedChange={(checked) => {
                    setTermsAccepted(checked);
                    if (checked) setTermsError(null);
                  }}
                  error={termsError || undefined}
                />
              </div>

              <button
                type="submit"
                form="checkout-form"
                className="hidden lg:flex w-full h-14 bg-brand text-brand-ink font-semibold text-[0.9rem] uppercase tracking-[0.12em] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-ink transition-[background] focus-visible-ring items-center justify-center"
                disabled={!isValid || !hasPrimaryContact || !termsValid || isCheckingEligibility}
                data-testid="checkout-continue"
              >
                {isCheckingEligibility
                  ? t("checkout.checkingEligibility")
                  : t("checkout.continueToPayment")}
              </button>
            </aside>
          </div>
        </div>
      </main>

      <footer className="border-t hairline-strong bg-white">
        <div className="mx-auto max-w-5xl px-6 py-5 flex items-center justify-between">
          <span className="text-xs text-ink-subtle">
            {t("common.poweredBy")}
          </span>
          <span className="text-xs text-ink-subtle tabular">
            © {new Date().getFullYear()} {tenant?.name}
          </span>
        </div>
      </footer>
    </div>
  );
}

export default CheckoutPage;
